import * as Select from '@radix-ui/react-select'
import clsx from 'clsx'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import React, { useState } from 'react'

const CurrentTag = () => (
  <div
    className='ml-2 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 align-middle'
  >
    Current
  </div>
)

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  {
    value: string;
    outputValue: string;
    currentValue: string;
    isMostRecentPeriod: boolean;
  }
>(({ value, outputValue, currentValue, isMostRecentPeriod }, forwardedRef) => {
  const [isFocused, setIsFocused] = useState(false)

  const isCurrentlySelected = value === currentValue
  return (
    <Select.Item
      className={clsx(
        'p-2 px-4 text-gray-500 flex items-center relative focus:outline-none cursor-pointer align-center content-center',
        { 'text-thatch-purple-secondary': isCurrentlySelected },
        { 'bg-thatch-purple-secondary text-white': isFocused },
        { 'bg-white': !isFocused }
      )}
      value={value}
      ref={forwardedRef}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      <Select.ItemText>
        {outputValue}
        {isMostRecentPeriod && <CurrentTag />}
      </Select.ItemText>
      <Select.ItemIndicator className='pl-2'>
        <CheckIcon />
      </Select.ItemIndicator>
    </Select.Item>
  )
})
SelectItem.displayName = 'SelectItem'

export const SelectPlanYear = ({
  planYearsAvailable,
  selectedPlanYear,
  handleSetPlanYear,
}: {
  planYearsAvailable: number[];
  selectedPlanYear: number;
  handleSetPlanYear: (value: string) => void;
}) => {
  // If only one plan year can be selected, only display the current plan year.
  if (planYearsAvailable.length < 2) {
    return (
      <div className='flex items-center shrink-0'>
        <div className='text-normal font-semibold text-ds-text-secondary py-2 pr-2'>
          Plan Year:
        </div>
        <div className='text-normal text-ds-text-secondary'>
          {selectedPlanYear}
        </div>
      </div>
    )
  }

  return (
    <div className='flex items-center shrink-0'>
      <Select.Root
        value={selectedPlanYear.toString()}
        onValueChange={handleSetPlanYear}
      >
        <div className='text-normal font-semibold text-ds-text-secondary py-2 pr-2'>
          Plan Year:
        </div>
        <Select.Trigger
          className='text-gray-500 inline-flex items-center justify-center rounded h-[2.25rem] px-4 bg-white focus:outline-none cursor-pointer shadow ring-1 ring-black ring-opacity-5 md:rounded-lg text-sm'
          aria-label='Plan Year Options'
        >
          <Select.Value placeholder='Select a plan year...'>
            {selectedPlanYear}
          </Select.Value>

          <Select.Icon className='pl-2'>
            <ChevronDownIcon />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position='popper'
            sideOffset={5}
            className='overflow-hidden rounded-md border'
          >
            <Select.ScrollUpButton />

            <Select.Viewport>
              {planYearsAvailable.map((planYear) => (
                <SelectItem
                  key={planYear}
                  value={planYear.toString()}
                  outputValue={planYear.toString()}
                  currentValue={selectedPlanYear.toString()}
                  isMostRecentPeriod={false}
                />
              ))}
            </Select.Viewport>

            <Select.ScrollDownButton />
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )
}
